/**
 * Static spec for the WagePro migration import template — the single source of
 * truth shared by the browser template builder, the browser header-matching /
 * mapping UI, the server staging validator, and every test. Keeping sheet
 * names, machine column names + human labels + aliases, per-column kind, enum
 * value lists, per-jurisdiction column sets, the ID-text column list, AND the
 * `MIGRATION_*` issue codes in ONE module is what stops the API and the
 * wagepro-web mirror from drifting (a membership test diffs the two).
 *
 * Pure data + types; no I/O, no runtime behaviour.
 */

import { EMPLOYMENT_TYPES } from './worker_type.js';

/** The worksheets of the template, in workbook order. */
export const MIGRATION_SHEET_NAMES = [
  'Instructions',
  'Employees',
  'Salary_History',
  'Payroll_History',
  'Leave_Balances',
  'Lists',
] as const;
export type MigrationSheetName = (typeof MIGRATION_SHEET_NAMES)[number];

/** The data sheets a validator classifies and reads (everything except the read-only tabs). */
export const MIGRATION_DATA_SHEETS = [
  'Employees',
  'Salary_History',
  'Payroll_History',
  'Leave_Balances',
] as const satisfies readonly MigrationSheetName[];

/** Jurisdictions whose statutory columns the template carries. */
export type MigrationJurisdiction = 'LK' | 'ID' | 'GB';

// ── Enum value lists (also feed the Lists sheet dropdowns) ───────────────────

/** Employee `pay_basis` values. */
export const PAY_BASIS_VALUES = ['salaried', 'hourly'] as const;

/** Employee `employment_type` values — the canonical stored set (see worker_type). */
export const EMPLOYMENT_TYPE_VALUES = EMPLOYMENT_TYPES;

/** Employee `residency_status` values (SL tax residency, #26653). */
export const RESIDENCY_STATUS_VALUES = ['resident', 'non_resident'] as const;

/** Employee `apit_employment_type` values (SL APIT Table 7 routing, ADR-168). */
export const APIT_EMPLOYMENT_TYPE_VALUES = ['primary', 'secondary'] as const;

/** Salary-history change `reason` values. */
export const SALARY_REASON_VALUES = [
  'hire',
  'promotion',
  'annual_review',
  'correction',
  'other',
] as const;

/** Indonesia PTKP (dependant-status) codes. */
export const PTKP_STATUS_VALUES = [
  'TK/0',
  'TK/1',
  'TK/2',
  'TK/3',
  'K/0',
  'K/1',
  'K/2',
  'K/3',
  'K/I/0',
  'K/I/1',
  'K/I/2',
  'K/I/3',
] as const;

/** Indonesia BPJS JKK (work-injury) risk classes. */
export const BPJS_JKK_RISK_CLASS_VALUES = [1, 2, 3, 4, 5] as const;

// ── Column spec ──────────────────────────────────────────────────────────────

export type MigrationColumnKind =
  | 'text'
  /** An identifier/account number: read as text, format-as-Text in the xlsx, sci-notation-blocked. */
  | 'id_text'
  | 'date'
  | 'money'
  | 'integer'
  | 'percent'
  | 'boolean'
  | 'enum';

/**
 * `required: true` — always required. `'conditional'` — requiredness depends on
 * other fields or jurisdiction and is enforced by the validator, not the schema
 * (e.g. `monthly_salary` only for salaried non-contractors). `false` — optional.
 */
export type MigrationRequiredness = true | false | 'conditional';

export interface MigrationColumn {
  /** Machine (header) name — stable, matched case/space/underscore-insensitively. */
  name: string;
  /** Human label shown in row 1 and in the mapping UI. */
  label: string;
  kind: MigrationColumnKind;
  required: MigrationRequiredness;
  /** Present only for this jurisdiction; omitted = all jurisdictions. */
  jurisdiction?: MigrationJurisdiction;
  /** Allowed values for an `enum` column. */
  enumValues?: readonly (string | number)[];
  /** Alternate header spellings accepted by header matching. */
  aliases?: readonly string[];
}

export interface MigrationSheetSpec {
  name: MigrationSheetName;
  /** Whether the whole sheet must be present for a valid import. */
  required: boolean;
  columns: readonly MigrationColumn[];
}

const EMPLOYEES_COLUMNS: readonly MigrationColumn[] = [
  {
    name: 'employee_number',
    label: 'Employee number',
    kind: 'id_text',
    required: true,
  },
  { name: 'full_name', label: 'Full name', kind: 'text', required: true },
  { name: 'email', label: 'Email', kind: 'text', required: false },
  {
    name: 'employment_start_date',
    label: 'Employment start date',
    kind: 'date',
    required: true,
    aliases: ['start_date', 'hire_date'],
  },
  {
    name: 'termination_date',
    label: 'Termination date',
    kind: 'date',
    required: false,
    aliases: ['end_date', 'leaving_date'],
  },
  {
    name: 'employment_type',
    label: 'Employment type',
    kind: 'enum',
    required: true,
    enumValues: EMPLOYMENT_TYPE_VALUES,
  },
  {
    name: 'fte_percent',
    label: 'FTE %',
    kind: 'percent',
    required: 'conditional',
  },
  {
    name: 'pay_basis',
    label: 'Pay basis',
    kind: 'enum',
    required: true,
    enumValues: PAY_BASIS_VALUES,
  },
  {
    name: 'monthly_salary',
    label: 'Monthly salary',
    kind: 'money',
    required: 'conditional',
  },
  {
    name: 'hourly_rate',
    label: 'Hourly rate',
    kind: 'money',
    required: 'conditional',
  },
  {
    name: 'pay_currency',
    label: 'Pay currency',
    kind: 'text',
    required: false,
    aliases: ['currency', 'pay_currency_code'],
  },
  { name: 'job_title', label: 'Job title', kind: 'text', required: false },
  { name: 'department', label: 'Department', kind: 'text', required: false },
  {
    name: 'manager',
    label: 'Manager',
    kind: 'text',
    required: false,
    aliases: ['reports_to'],
  },
  {
    name: 'work_location',
    label: 'Work location',
    kind: 'text',
    required: false,
  },
  { name: 'bank_name', label: 'Bank name', kind: 'text', required: false },
  { name: 'bank_branch', label: 'Bank branch', kind: 'text', required: false },
  { name: 'bank_code', label: 'Bank code', kind: 'id_text', required: false },
  {
    name: 'bank_account_number',
    label: 'Bank account number',
    kind: 'id_text',
    required: false,
  },
  // Sri Lanka
  {
    name: 'nic_number',
    label: 'NIC number',
    kind: 'id_text',
    required: 'conditional',
    jurisdiction: 'LK',
  },
  {
    name: 'epf_member_no',
    label: 'EPF member number',
    kind: 'id_text',
    required: 'conditional',
    jurisdiction: 'LK',
  },
  {
    name: 'residency_status',
    label: 'Residency status',
    kind: 'enum',
    required: false,
    enumValues: RESIDENCY_STATUS_VALUES,
    jurisdiction: 'LK',
  },
  {
    name: 'is_citizen',
    label: 'Is citizen',
    kind: 'boolean',
    required: false,
    jurisdiction: 'LK',
  },
  {
    name: 'apit_employment_type',
    label: 'APIT employment (primary/secondary)',
    kind: 'enum',
    required: false,
    enumValues: APIT_EMPLOYMENT_TYPE_VALUES,
    jurisdiction: 'LK',
  },
  {
    name: 'apit_declared_primary_income',
    label: 'Declared primary income (monthly)',
    kind: 'money',
    required: false,
    jurisdiction: 'LK',
  },
  {
    name: 'apit_borne_by_employer',
    label: 'Income tax borne by employer (IRD Table 06)',
    kind: 'boolean',
    required: false,
    jurisdiction: 'LK',
  },
  // Indonesia
  {
    name: 'nik',
    label: 'NIK',
    kind: 'id_text',
    required: 'conditional',
    jurisdiction: 'ID',
  },
  {
    name: 'npwp',
    label: 'NPWP',
    kind: 'id_text',
    required: false,
    jurisdiction: 'ID',
  },
  {
    name: 'ptkp_status',
    label: 'PTKP status',
    kind: 'enum',
    required: 'conditional',
    jurisdiction: 'ID',
    enumValues: PTKP_STATUS_VALUES,
  },
  {
    name: 'bpjs_jkk_risk_class',
    label: 'BPJS JKK risk class',
    kind: 'enum',
    required: false,
    jurisdiction: 'ID',
    enumValues: BPJS_JKK_RISK_CLASS_VALUES,
  },
  {
    name: 'province_code',
    label: 'Province code',
    kind: 'id_text',
    required: false,
    jurisdiction: 'ID',
  },
  // United Kingdom
  {
    name: 'uk_tax_code',
    label: 'UK tax code',
    kind: 'id_text',
    required: 'conditional',
    jurisdiction: 'GB',
  },
  {
    name: 'uk_ni_category',
    label: 'UK NI category',
    kind: 'id_text',
    required: 'conditional',
    jurisdiction: 'GB',
  },
  {
    name: 'uk_is_director',
    label: 'UK is director',
    kind: 'boolean',
    required: false,
    jurisdiction: 'GB',
  },
];

const SALARY_HISTORY_COLUMNS: readonly MigrationColumn[] = [
  {
    name: 'employee_number',
    label: 'Employee number',
    kind: 'id_text',
    required: true,
  },
  {
    name: 'effective_from',
    label: 'Effective from',
    kind: 'date',
    required: true,
  },
  {
    name: 'pay_basis',
    label: 'Pay basis',
    kind: 'enum',
    required: true,
    enumValues: PAY_BASIS_VALUES,
  },
  {
    name: 'monthly_salary',
    label: 'Monthly salary',
    kind: 'money',
    required: 'conditional',
  },
  {
    name: 'hourly_rate',
    label: 'Hourly rate',
    kind: 'money',
    required: 'conditional',
  },
  {
    name: 'reason',
    label: 'Reason',
    kind: 'enum',
    required: false,
    enumValues: SALARY_REASON_VALUES,
  },
  { name: 'note', label: 'Note', kind: 'text', required: false },
];

const PAYROLL_HISTORY_COLUMNS: readonly MigrationColumn[] = [
  {
    name: 'employee_number',
    label: 'Employee number',
    kind: 'id_text',
    required: true,
  },
  { name: 'period_start', label: 'Period start', kind: 'date', required: true },
  { name: 'period_end', label: 'Period end', kind: 'date', required: true },
  { name: 'basic_pay', label: 'Basic pay', kind: 'money', required: true },
  { name: 'gross_pay', label: 'Gross pay', kind: 'money', required: true },
  { name: 'overtime', label: 'Overtime', kind: 'money', required: false },
  { name: 'bonus', label: 'Bonus', kind: 'money', required: false },
  { name: 'lump_sum', label: 'Lump sum', kind: 'money', required: false },
  {
    name: 'unpaid_leave_deduction',
    label: 'Unpaid leave deduction',
    kind: 'money',
    required: false,
  },
  {
    name: 'other_deductions',
    label: 'Other deductions',
    kind: 'money',
    required: false,
  },
  {
    name: 'hours_worked',
    label: 'Hours worked',
    kind: 'integer',
    required: false,
  },
  { name: 'net_pay', label: 'Net pay', kind: 'money', required: true },
  // Sri Lanka statutory
  {
    name: 'employee_epf',
    label: 'Employee EPF',
    kind: 'money',
    required: false,
    jurisdiction: 'LK',
  },
  {
    name: 'employer_epf',
    label: 'Employer EPF',
    kind: 'money',
    required: false,
    jurisdiction: 'LK',
  },
  {
    name: 'etf',
    label: 'ETF',
    kind: 'money',
    required: false,
    jurisdiction: 'LK',
  },
  {
    name: 'apit',
    label: 'APIT',
    kind: 'money',
    required: false,
    jurisdiction: 'LK',
  },
  {
    name: 'apit_lump_sum',
    label: 'APIT lump sum',
    kind: 'money',
    required: false,
    jurisdiction: 'LK',
  },
  // Indonesia statutory (THR is OUTSIDE gross — B5)
  {
    name: 'pph21',
    label: 'PPh21',
    kind: 'money',
    required: false,
    jurisdiction: 'ID',
  },
  {
    name: 'bpjs_employee_jht',
    label: 'BPJS employee JHT',
    kind: 'money',
    required: false,
    jurisdiction: 'ID',
  },
  {
    name: 'bpjs_employee_jp',
    label: 'BPJS employee JP',
    kind: 'money',
    required: false,
    jurisdiction: 'ID',
  },
  {
    name: 'bpjs_employee',
    label: 'BPJS employee',
    kind: 'money',
    required: false,
    jurisdiction: 'ID',
  },
  {
    name: 'bpjs_employer',
    label: 'BPJS employer',
    kind: 'money',
    required: false,
    jurisdiction: 'ID',
  },
  {
    name: 'thr',
    label: 'THR',
    kind: 'money',
    required: false,
    jurisdiction: 'ID',
  },
];

const LEAVE_BALANCES_COLUMNS: readonly MigrationColumn[] = [
  {
    name: 'employee_number',
    label: 'Employee number',
    kind: 'id_text',
    required: true,
  },
  {
    name: 'leave_type_code',
    label: 'Leave type code',
    kind: 'text',
    required: true,
  },
  {
    name: 'balance_days',
    label: 'Balance (days)',
    kind: 'money',
    required: true,
  },
  {
    name: 'as_of_date',
    label: 'As-of date',
    kind: 'date',
    required: true,
    aliases: ['cutover_date'],
  },
];

/** Every data-sheet's column spec, keyed by sheet name. */
export const MIGRATION_SHEETS: Record<
  (typeof MIGRATION_DATA_SHEETS)[number],
  MigrationSheetSpec
> = {
  Employees: { name: 'Employees', required: true, columns: EMPLOYEES_COLUMNS },
  Salary_History: {
    name: 'Salary_History',
    required: false,
    columns: SALARY_HISTORY_COLUMNS,
  },
  Payroll_History: {
    name: 'Payroll_History',
    required: false,
    columns: PAYROLL_HISTORY_COLUMNS,
  },
  Leave_Balances: {
    name: 'Leave_Balances',
    required: false,
    columns: LEAVE_BALANCES_COLUMNS,
  },
};

/**
 * Columns that must be formatted as Text in the xlsx template (so leading zeros
 * survive and no value becomes scientific notation), and whose numeric-cell
 * uploads are a sci-notation / lost-leading-zero warning.
 */
export function idTextColumns(sheet: MigrationSheetSpec): string[] {
  return sheet.columns.filter((c) => c.kind === 'id_text').map((c) => c.name);
}

/** The columns in play for a given jurisdiction (jurisdiction-less + matching). */
export function columnsForJurisdiction(
  sheet: MigrationSheetSpec,
  jurisdiction: MigrationJurisdiction,
): MigrationColumn[] {
  return sheet.columns.filter(
    (c) => c.jurisdiction === undefined || c.jurisdiction === jurisdiction,
  );
}

// ── Issue codes ──────────────────────────────────────────────────────────────

export type MigrationIssueSeverity = 'error' | 'warning' | 'info';

/**
 * Every `MIGRATION_*` issue code. The wagepro-web client mirrors this union as
 * a code→i18n-key map; a test diffs the two sets so a new code can never ship
 * without a translation (and vice versa).
 */
export const MIGRATION_ISSUE_CODES = [
  // File-level
  'MIGRATION_FILE_UNREADABLE',
  'MIGRATION_BAD_ENCODING',
  'MIGRATION_MISSING_SHEET',
  'MIGRATION_MISSING_COLUMN',
  'MIGRATION_DUPLICATE_HEADER',
  'MIGRATION_EMPTY_TEMPLATE',
  'MIGRATION_UNKNOWN_SHEET',
  // Cell-level
  'MIGRATION_REQUIRED',
  'MIGRATION_INVALID_VALUE',
  'MIGRATION_INVALID_ENUM',
  'MIGRATION_TOO_LONG',
  'MIGRATION_INVALID_DATE',
  'MIGRATION_FUTURE_DATE',
  'MIGRATION_BAD_MONEY',
  'MIGRATION_NEGATIVE_MONEY',
  'MIGRATION_TOO_MANY_DECIMALS',
  'MIGRATION_SCI_NOTATION',
  'MIGRATION_LOST_LEADING_ZERO',
  'MIGRATION_INVALID_EMAIL',
  'MIGRATION_INVALID_NIC',
  'MIGRATION_INVALID_NIC_DAY',
  'MIGRATION_INVALID_EPF',
  'MIGRATION_INVALID_NIK',
  'MIGRATION_INVALID_NPWP',
  'MIGRATION_INVALID_PTKP',
  'MIGRATION_INVALID_RISK_CLASS',
  // Row-level
  'MIGRATION_NET_IDENTITY',
  'MIGRATION_GROSS_SUM',
  'MIGRATION_PERIOD_OUTSIDE_EMPLOYMENT',
  'MIGRATION_CONTRACTOR_IN_PAYROLL',
  // Cross-row
  'MIGRATION_DUPLICATE_EMPLOYEE_NUMBER',
  'MIGRATION_DUPLICATE_EMPLOYEE_PERIOD',
  'MIGRATION_DUPLICATE_LEAVE_TYPE',
  'MIGRATION_DUPLICATE_SALARY_DATE',
  'MIGRATION_UNKNOWN_EMPLOYEE',
  'MIGRATION_INCONSISTENT_PERIOD',
  'MIGRATION_MISSING_MONTH',
  'MIGRATION_YTD_ROW_REJECTED',
  'MIGRATION_SALARY_MISMATCH',
  // Statutory warnings
  'MIGRATION_EPF_RATE_WARNING',
  'MIGRATION_ETF_RATE_WARNING',
  'MIGRATION_TABLE5_WARNING',
  'MIGRATION_APIT_PERIOD_UNSUPPORTED',
  'MIGRATION_PPH21_TER_WARNING',
  'MIGRATION_BPJS_DERIVE_WARNING',
  // Company-level (blocking unless noted)
  'MIGRATION_PLAN_CAP',
  'MIGRATION_OVERLAPPING_RUNS',
  'MIGRATION_CURRENCY_NOT_ENABLED',
  'MIGRATION_UNKNOWN_LEAVE_TYPE',
  'MIGRATION_NIC_CLASH',
  'MIGRATION_NIK_CLASH',
  'MIGRATION_NPWP_CLASH',
  'MIGRATION_EMAIL_CLASH',
  'MIGRATION_EMPLOYEE_EXISTS',
] as const;

export type MigrationIssueCode = (typeof MIGRATION_ISSUE_CODES)[number];

/**
 * A single validation issue. `params` NEVER echoes a NIC / NIK / NPWP / bank
 * number (the message is translated client-side from `code` + `params`).
 */
export interface MigrationIssue {
  sheet: MigrationSheetName | 'file';
  /** Excel row number (header = 1); 0 = file-level. */
  row: number;
  column?: string;
  code: MigrationIssueCode;
  severity: MigrationIssueSeverity;
  params?: Record<string, string | number>;
}
